import {ActionContext, ActionTree} from "vuex";
import {Product} from "../../../../domain/entities/shop/product/product";
import {Filter} from "../../../../domain/entities/shop/filter/filter";
import {Receipt} from "../../../../domain/entities/shop/order/receipt";
import {Prices} from "../../../../domain/entities/shop/filter/prices";
import {Order} from "../../../../domain/entities/shop/order/order";
import {ListResponse} from "../../../../domain/entities/shop/product/listResponse";
import shopUseCase from "../../../../domain/usecase/impl/shopUseCase";

class ShopState {
  /** State used for pagination of view shop products.*/
  productsPages = 0;

  /** State of the products listing in the products view. */
  products: Array<Product> = Array<Product>();

  /** Controls the current state of the product. */
  product = new Product();

  /** State to list the products added to the cart. */
  productsInBag: Array<Product> = Array<Product>();

  /** State to list all products the user has favorite. */
  favoriteProducts: Array<Product> = Array<Product>();

  /** State used for pagination of products in the favorite list. */
  page = 0;

  /** State to list all filter items. */
  filter = new Filter();

  /**The state to keep track of the selected options in the filter. */
  currentFilter = new Filter();

  /** State of the maximum number entered by the user in the filter input. */
  maxPrice = 0;

  /** State of the minimum number entered by the user in the filter input. */
  minPrice = 0;

  /** Receipt returned from the order post. */
  receiptOrder = new Receipt();

  /** Listing of user orders, information is taken by user cookie. */
  ordersHistory: Array<Receipt> = Array<Receipt>();

  /** Receipt returned from get by the product id.*/
  currentReceipt = new Receipt();

  /** State used to generate the receipt in the order list. */
  receipt = new Receipt();

  /** State that makes the loading view visible.*/
  loading = false;

  /** State with updated user's decoins. */
  decoinsInWallet = 0;
}

const state = new ShopState();

const mutations = {
  /** Mutation responsible for setting the state product with a product. Used in the getProduct action.
   * @param state
   * @param product parameter that takes an object of type Product.
   * @see Product */
  SET_PRODUCT(state: ShopState, product: Product): void {
    state.product = product;
  },

  /** Mutation responsible for receiving an array of products. Used to set the products that will be loaded in the view.
   * @param state
   * @param products parameter that takes an array of objects of type Product.
   * @see Product */
  LOAD_PRODUCTS(state: ShopState, products: Array<Product>): void {
    state.products = products;
  },

  /** Mutation responsible for setting the state products with the registered products.
   * @param state
   * @param products parameter that takes an array of objects of type Product.
   * @see Product */
  PRODUCTS(state: ShopState, products: Array<Product>): void {
    state.products = products;
  },

  /** Mutation that receives an array of products that are in the favorites list.
   * @param state
   * @param products parameter that takes an array of objects of type Product.
   * @see Product */
  LOAD_FAVORITE_PRODUCTS(state: ShopState, products: Array<Product>): void {
    state.favoriteProducts = products;
  },

  /** Mutation responsible for setting the productsInBag state with the products that were added to the bag.
   * @param state
   * @param products parameter that receives an array of objects of type Product
   * @see Product */
  LOAD_BAG(state: ShopState, products: Array<Product>): void {
    state.productsInBag = products;
  },

  /** Mutation responsible for setting the product in the bag.
   * @param state
   * @param products parameter that receives an array of objects of type Product.
   * @see Product */
  SET_PRODUCTS(state: ShopState, products: Array<Product>): void {
    state.productsInBag = products;
  },

  /** Mutation responsible for setting the state receipt with the receipt returned when an order is finalized.
   * @param state
   * @param receipt parameter that receives an object of type Receipt.
   * @see Receipt */
  RECEIPT_ORDER(state: ShopState, receipt: Receipt): void {
    state.receiptOrder = receipt;
  },

  /** Mutation responsible for setting the receiptOrderHistory state.
   * @param state
   * @param receipt parameter that receives an object of type Receipt.
   * @see Receipt */
  SET_RECEIPT(state: ShopState, receipt: Receipt): void {
    state.receipt = receipt;
  },

  /** Mutation responsible for setting the currentReceipt state with the receipt generated at the time of finalizing order.
   * @param state
   * @param currentReceipt parameter that receives an object of type Receipt.
   * @see Receipt */
  CURRENT_RECEIPT(state: ShopState, currentReceipt: Receipt): void {
    state.currentReceipt = currentReceipt;
  },

  /** Mutation responsible for setting the order history.
   * @param state
   * @param orders parameter that receives an array of objects of type Receipt.
   * @see Receipt*/
  ORDERS_HISTORY(state: ShopState, orders: Array<Receipt>): void {
    state.ordersHistory = orders;
  },

  /** Mutation responsible for setting the state filter with the filters returned from the request.
   * @param state
   * @param filter parameter that receives an object of type Filter.
   * @see Filter */
  LOAD_FILTERS(state: ShopState, filter: Filter): void {
    state.filter = filter;
  },

  /** Mutation that sets the current filters that the user has selected.
   * @param state
   * @param filter parameter that receives an object of type Filter.
   * @see Filter */
  SET_CURRENT_FILTER(state: ShopState, filter: Filter): void {
    state.currentFilter = filter;
  },

  /** Mutation responsible for setting the minimum value in state.
   * @param state
   * @param min parameter that takes a variable of type number. */
  SET_MIN_PRICE(state: ShopState, min: number): void {
    state.minPrice = min;
  },

  /** Mutation responsible for setting the maximum value in the state.
   * @param state
   * @param max parameter that takes a variable of type number. */
  SET_MAX_PRICE(state: ShopState, max: number): void {
    state.maxPrice = max;
  },

  /** Mutation that sets the pagination value of the products.
   * @param state
   * @param pages parameter that takes a variable of number. */
  SET_PRODUCTS_PAGES(state: ShopState, pages: number): void {
    state.productsPages = pages;
  },

  /** Mutation that sets the pagination value of the products in the favorites list.
   * @param state
   * @param page number of pages. */
  SET_FAVORITE_PRODUCTS_PAGE(state: ShopState, page: number): void {
    state.page = page;
  },

  /** Mutation that sets the loading state.
   * @param state
   * @param isVisible parameter that takes a boolean variable.*/
  SET_LOADING(state: ShopState, isVisible: boolean): void {
    state.loading = isVisible;
  },

  /** Mutation that sets the state decoinsInWallet with the updated user's decoins.
   * @param state
   * @param decoinsInWallet User decoins available. */
  SET_DECOINS_IN_WALLET(state: ShopState, decoinsInWallet: number): void {
    state.decoinsInWallet = decoinsInWallet;
  }
};

const actions: ActionTree<ShopState, ShopState> = {
  /** Action responsible for getting the information of a product.
   * @param commit
   * @param id parameter that receives the product id. */
  async getProduct({ commit }: ActionContext<ShopState, ShopState>, id: number): Promise<Product | void> {
    const product: Product = await shopUseCase.getProduct(id);
    commit("SET_PRODUCT", product);
    return product;
  },

  /** Action responsible for loading all registered products. Used to list products with filters.
   * @param commit
   * @param payload object that has an object of type Filter, variable page of type number,
   * min variable of type number or null, min variable of type number or null.
   * @see Filter */
  async loadProducts(
    { commit }: ActionContext<ShopState, ShopState>,
    payload: {
      filter: Filter;
      page: number;
      min: number | null;
      max: number | null;
    }
  ): Promise<void> {
    /** Variable prices receives a list of all prices selected in the filter by the user.
     * Then a new object of type Prices is added to the prices variable instantiating the min and max variables
     * with the value passed by parameters in the action.
     * After that, the request is made passing the Filter object with the populated values and the current page. */
    const prices = [...payload.filter.prices];
    if (payload.min || payload.max) {
      prices.push(new Prices(0, "", payload.min, payload.max));
    }
    const listResponse = await shopUseCase.filterProducts(
      payload.page,
      new Filter(payload.filter.shops, prices, payload.filter.categories, payload.filter.searchQuery)
    );

    commit("SET_PRODUCTS_PAGES", listResponse.page);
    commit("LOAD_PRODUCTS", listResponse.items);
  },

  /** Action responsible for loading all registered products without filters. */
  async products({ commit }: ActionContext<ShopState, ShopState>): Promise<void> {
    const products: ListResponse<Product> = await shopUseCase.products();
    commit("PRODUCTS", products.items);
  },

  /** Action responsible for increasing the quantity of the product added to the cart and updating the localStorage
   * with the current amount.
   * @param commit
   * @param product parameter that takes an object of type Product.
   * @see Product */
  async increaseProduct({ commit }: ActionContext<ShopState, ShopState>, product: Product): Promise<void> {
    const bagProducts: Array<Product> = shopUseCase.getProductsFromBag();
    const bagProduct = bagProducts.find(
      bagProduct => bagProduct.id === product.id
    );
    if (bagProduct) {
      bagProduct.quantity++;
    }
    await shopUseCase.setProductsToBag(bagProducts);
  },

  /** Action responsible for decreasing the quantity of the product added to the cart and updating the localStorage
   * with the current amount.
   * @param commit
   * @param product parameter that takes an object of type Product.
   * @see Product */
  async decreaseProduct({ commit }: ActionContext<ShopState, ShopState>, product: Product): Promise<void> {
    const bagProducts: Array<Product> = shopUseCase.getProductsFromBag();
    const bagProduct = bagProducts.find(
      bagProduct => bagProduct.id === product.id
    );
    if (bagProduct) {
      bagProduct.quantity--;
    }
    await shopUseCase.setProductsToBag(bagProducts);
  },

  /** Action responsible for adding a product to favorites.
   * @param commit
   * @param id product id to add to favorites. */
  async addToFavorite({ commit }: ActionContext<ShopState, ShopState>, id: number): Promise<void> {
    const product = await shopUseCase.addToFavorite(id);
    commit("SET_FAVORITE_PRODUCT", product);
  },

  /** Action responsible for loading all products that are in the favorites list.
   * This action expects as a parameter the payload object that serves to set the number of pages
   * and the limit of products per page.
   * @param commit
   * @param payload object that takes two variables of type number. */
  async loadFavoriteProducts(
    { commit }: ActionContext<ShopState, ShopState>,
    payload: { page: number; limit: number }
  ): Promise<void> {
    const product: ListResponse<Product> = await shopUseCase.listFavoriteProducts(
      payload.page,
      payload.limit
    );
    commit("LOAD_FAVORITE_PRODUCTS", product.items);
    commit("SET_FAVORITE_PRODUCTS_PAGE", product.page);
  },

  /** Action responsible for removing the product from the favorites list.
   * @param commit
   * @param id parameter that receives the id of the product to remove. */
  async removeProductFromFavorites(
    { commit }: ActionContext<ShopState, ShopState>,
    id: number
  ): Promise<void> {
    await shopUseCase.removeProductFromFavorites(id);
  },

  /** Action responsible for loading the products added to the bag. */
  loadBagProducts: function({ commit }: ActionContext<ShopState, ShopState>): void {
    const products: Array<Product> = shopUseCase.getProductsFromBag();
    commit("LOAD_BAG", products);
  },

  /** Action responsible for adding a product to the bag.
   * @param commit
   * @param product parameter that takes an object of type Product.
   * @see Product */
  addToBag({ commit }: ActionContext<ShopState, ShopState>, product: Product): void {
    shopUseCase.setProductToBag(product);
    const products: Array<Product> = shopUseCase.getProductsFromBag();
    commit("SET_PRODUCTS", products);
  },

  /** Action responsible for removing a product in the bag from its id.
   * @param commit
   * @param product parameter that takes a variable of type number. */
  async removeFromBag({ commit }: ActionContext<ShopState, ShopState>, product: Product): Promise<void> {
    await shopUseCase.removeBagProduct(product);
    const products: Array<Product> = shopUseCase.getProductsFromBag();
    commit("SET_PRODUCTS", products);
  },

  /** Action responsible for removing all products from the bag when a purchase is completed.*/
  async removeAllProductsFromBag({ commit }: ActionContext<ShopState, ShopState>): Promise<void> {
    await shopUseCase.removeAllBagProducts();
    const products: Array<Product> = shopUseCase.getProductsFromBag();
    commit("SET_PRODUCTS", products);
  },

  /** Action responsible for making a post of the order for the request.
   * The request will return the purchase receipt. This receipt is used to set the order id in the URl in the shop view.
   * Placing the id in the URl it is possible to have the order information.
   * @param commit
   * @param order parameter that takes an object of type Order.
   * @see Order */
  async order({ commit }: ActionContext<ShopState, ShopState>, order: Order): Promise<void> {
    const response: Receipt = await shopUseCase.order(order);
    commit("RECEIPT_ORDER", response);
  },

  /** Action responsible for loading user orders. */
  async ordersHistory({ commit }: ActionContext<ShopState, ShopState>): Promise<void> {
    const response: Array<Receipt> = await shopUseCase.orders();
    commit("ORDERS_HISTORY", response);
  },

  /** Action responsible for getting the current information of the order. The order id is taken from the URL.
   * @param commit
   * @param orderID parameter that receives the id of the order. */
  async getReceipt({ commit }: ActionContext<ShopState, ShopState>, orderID: number): Promise<void> {
    const response: Receipt = await shopUseCase.getReceipt(orderID);
    commit("CURRENT_RECEIPT", response);
  },

  /** Action used in the orderHistory view to regenerate the receipt.
   * @param commit
   * @param orderID parameter that receives the id of the order. */
  async createReceipt({ commit }: ActionContext<ShopState, ShopState>, orderID: number): Promise<void> {
    const response: Receipt = await shopUseCase.createReceipt(orderID);
    commit("SET_RECEIPT", response);
  },

  /** Action responsible for loading the entities that the products can filter on.
   * @param commit
   * @param filter parameter that receives an object of type Filter.
   * @see Filter */
  async loadFilters({ commit }: ActionContext<ShopState, ShopState>, filter: Filter): Promise<void> {
    const filters: Filter = await shopUseCase.listFilters(filter);
    commit("LOAD_FILTERS", filters);
  },

  /** Action responsible for setting the minimum value entered by the user in the filter input.
   * @param commit
   * @param min parameter that receives a variable of type number*/
  setMinPrice({ commit }: ActionContext<ShopState, ShopState>, min: number): void {
    commit("SET_MIN_PRICE", min);
  },

  /** Action responsible for setting the maximum value entered by the user in the filter input.
   * @param commit
   * @param max parameter that receives a variable of type number*/
  setMaxPrice({ commit }: ActionContext<ShopState, ShopState>, max: number): void {
    commit("SET_MAX_PRICE", max);
  },

  /** Action responsible for setting the filters selected by the user.
   * @param commit
   * @param filter parameter that receives an object of type Filter.
   * @see Filter */
  currentFilter({ commit }: ActionContext<ShopState, ShopState>, filter: Filter): void {
    commit("SET_CURRENT_FILTER", filter);
  },

  /** Action responsible for setting the value entered by the user in the search bar. */
  async setInputFilter({ commit }: ActionContext<ShopState, ShopState>, payload: { filter: Filter; page: number; }): Promise<void> {
    const listResponse = await shopUseCase.filterProducts(
      payload.page,
      new Filter(payload.filter.shops, payload.filter.prices, payload.filter.categories, payload.filter.searchQuery)
    );

    commit("SET_PRODUCTS_PAGES", listResponse.page);
    commit("LOAD_PRODUCTS", listResponse.items);
  },

  /** Action responsible for making the loading view visible.
   * @param commit
   * @param isVisible parameter that takes a boolean variable. */
  setLoading({ commit }: ActionContext<ShopState, ShopState>, isVisible: boolean) {
    commit("SET_LOADING", isVisible);
  },

  /** Action responsible for receiving the user's decoins.
   * @param commit
   * @param decoins total user decoins. */
  setDecoinsInWallet({ commit }: ActionContext<ShopState, ShopState>, decoins: number): void {
    commit("SET_DECOINS_IN_WALLET", decoins);
  }
};

const getters = {
  /** Getter of the number of pages per products. */
  productsPages: (state: ShopState): number => state.productsPages,

  /** Getter of a product. */
  product: (state: ShopState): Product => state.product,

  /** Getter of an array of products. */
  products: (state: ShopState): Array<Product> => state.products,

  /** Getter of the products in the favorites list. */
  productsInFavoriteList: (state: ShopState): Array<Product> =>
    state.favoriteProducts,

  /** Getter of the number of pages of the product listing in favorites. */
  page: (state: ShopState): number => state.page,

  /** Getter of the products in the bag. */
  productsInBag: (state: ShopState): Array<Product> => state.productsInBag,

  /** Filter entities getter. */
  filter: (state: ShopState): Filter => state.filter,

  /** Getter of the entities selected in the filters. */
  currentFilter: (state: ShopState): Filter => state.currentFilter,

  /** Getter of the maximum value entered by the user in the filters input. */
  maxPrice: (state: ShopState): number => state.maxPrice,

  /** Getter of the minimum value entered by the user in the filters input. */
  minPrice: (state: ShopState): number => state.minPrice,

  /** Receipt getter returned at checkout. */
  receiptOrder: (state: ShopState): Receipt => state.receiptOrder,

  /** User's order history getter. */
  ordersHistory: (state: ShopState): Array<Receipt> => state.ordersHistory,

  /** Receipt getter, used no to display the order details when the purchase is completed. */
  currentReceipt: (state: ShopState): Receipt => state.currentReceipt,

  /** Receipt getter, used in the order history to generate a new receipt, in case it was not generated at the time of finalizing the purchase. */
  receipt: (state: ShopState): Receipt => state.receipt,

  /** View loading view state getter */
  loading: (state: ShopState): boolean => state.loading,

  /** Getter of the decoins with the subtraction of the values of the products added in the bag. */
  balance: (state: ShopState): number => {
    let total = 0;
    for (const it of state.productsInBag) {
      total += it.decoins * it.quantity;
    }
    return state.decoinsInWallet - total;
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
